import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { LiveDisplayService } from '@api/live-display.service';
import {
  LiveDisplayOverviewVM,
  LiveDisplayLocationVM,
  LiveDisplayTrendVM,
  SiteVM
} from '@models/backend-generated-models/models';
import { SiteStateService } from '@states/site-state.service';
import { Color, ScaleType } from '@swimlane/ngx-charts';

export interface ChartSeriesItem {
  name: string;
  value: number;
}

export interface ChartSeriesGroup {
  name: string;
  series: ChartSeriesItem[];
}

export interface UtilityPanelUI {
  config: LiveDisplayOverviewVM;
  chartResults: ChartSeriesGroup[];
  chartColorScheme: Color;
}

@Component({
  selector: 'app-live-display',
  templateUrl: './live-display.component.html',
  styleUrls: ['./live-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiveDisplayComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // State
  siteId?: number;
  electricityPanel: UtilityPanelUI | null = null;
  gasPanel: UtilityPanelUI | null = null;

  // Chart color schemes
  electricityColorScheme: Color = {
    name: 'electricityScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#58a6ff', '#8b949e'] // Today (cyan/blue), Same Day Last Week (muted gray)
  };

  gasColorScheme: Color = {
    name: 'gasScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#f0883e', '#8b949e'] // Today (orange/amber), Same Day Last Week (muted gray)
  };

  // Dedicated 3-hour tick marks for TV dashboard presentation
  xAxisTicks: string[] = [
    '00:00',
    '03:00',
    '06:00',
    '09:00',
    '12:00',
    '15:00',
    '18:00',
    '21:00'
  ];

  // Loading / Error states
  isLoading: boolean = true;
  errorMessage: string | null = null;
  lastRefreshedAt: Date = new Date();

  // Fullscreen state
  isFullscreen: boolean = false;

  // Kiosk mode state
  isKiosk: boolean = false;
  private kioskToken: string | null = null;

  constructor(
    private liveDisplayService: LiveDisplayService,
    private siteStateService: SiteStateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isKiosk = this.router.url.startsWith('/live-display/kiosk');
    if (this.isKiosk) {
      const deviceKey = localStorage.getItem('kiosk_device_key');
      if (!deviceKey) {
        this.router.navigate(['/live-display/kiosk/setup']);
        return;
      }
      this.initKioskStream(deviceKey);
    } else {
      this.initStandardStream();
    }
  }

  private initKioskStream(deviceKey: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.liveDisplayService.exchangeKioskToken$(deviceKey).pipe(
      switchMap(res => {
        this.kioskToken = res.token;
        this.siteId = res.siteId;
        sessionStorage.setItem('kiosk_jwt', res.token);

        // Periodic token renewal every 10 minutes (token valid for 15 minutes)
        timer(10 * 60 * 1000, 10 * 60 * 1000).pipe(
          switchMap(() => this.liveDisplayService.exchangeKioskToken$(deviceKey).pipe(
            catchError(() => EMPTY)
          )),
          takeUntil(this.destroy$)
        ).subscribe(renewalRes => {
          this.kioskToken = renewalRes.token;
          sessionStorage.setItem('kiosk_jwt', renewalRes.token);
        });

        // 30s Polling loop for kiosk overview data
        return timer(0, 30000).pipe(
          switchMap(() => {
            this.lastRefreshedAt = new Date();
            const token = this.kioskToken || sessionStorage.getItem('kiosk_jwt') || '';
            return this.liveDisplayService.getKioskOverview$(token).pipe(
              catchError(err => {
                if (err.status === 401) {
                  return this.liveDisplayService.exchangeKioskToken$(deviceKey).pipe(
                    switchMap(retryRes => {
                      this.kioskToken = retryRes.token;
                      sessionStorage.setItem('kiosk_jwt', retryRes.token);
                      return this.liveDisplayService.getKioskOverview$(retryRes.token);
                    }),
                    catchError(() => {
                      this.isLoading = false;
                      this.errorMessage = 'Kiosk authentication expired or invalid. Please reconfigure device key.';
                      this.cdr.markForCheck();
                      return EMPTY;
                    })
                  );
                }
                this.isLoading = false;
                this.errorMessage = 'Unable to load live display data. Retrying...';
                this.cdr.markForCheck();
                return EMPTY;
              })
            );
          })
        );
      }),
      catchError(err => {
        this.isLoading = false;
        if (err.status === 401 || err.status === 400) {
          this.errorMessage = 'Invalid or disabled Kiosk Device Key. Please verify device configuration.';
          this.router.navigate(['/live-display/kiosk/setup']);
        } else {
          this.errorMessage = 'Unable to connect to Carbon server. Retrying...';
        }
        this.cdr.markForCheck();
        return EMPTY;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: LiveDisplayOverviewVM[]) => {
        this.processDualUtilityData(data || []);
        this.isLoading = false;
        this.errorMessage = null;
        this.cdr.markForCheck();
      }
    });
  }

  private initStandardStream(): void {
    // Drive live display polling strictly from the valid active site
    this.siteStateService.selectedSite$
      .pipe(
        filter((site): site is SiteVM => !!site && typeof site.siteId === 'number' && site.siteId > 0),
        distinctUntilChanged((prev, curr) => prev?.siteId === curr?.siteId),
        switchMap(site => {
          this.siteId = site.siteId!;
          this.isLoading = true;
          this.errorMessage = null;
          this.cdr.markForCheck();

          // Polling loop for active site (30s refresh)
          return timer(0, 30000).pipe(
            switchMap(() => {
              this.lastRefreshedAt = new Date();

              return this.liveDisplayService.getOverview$(this.siteId).pipe(
                catchError(err => {
                  this.isLoading = false;
                  this.errorMessage = 'Unable to load live display data. Retrying...';
                  this.cdr.markForCheck();
                  return EMPTY;
                })
              );
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: LiveDisplayOverviewVM[]) => {
          this.processDualUtilityData(data || []);
          this.isLoading = false;
          this.errorMessage = null;
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processDualUtilityData(utilities: LiveDisplayOverviewVM[]): void {
    if (!utilities.length) {
      this.electricityPanel = null;
      this.gasPanel = null;
      return;
    }

    // Find electricity (or first utility)
    const elecConfig = utilities.find(u => u.utilityType?.toLowerCase() === 'electricity') || utilities[0];
    // Find gas (or second utility)
    const gasConfig = utilities.find(u => u.utilityType?.toLowerCase() === 'gas') || (utilities.length > 1 ? utilities[1] : null);

    if (elecConfig) {
      this.electricityPanel = {
        config: elecConfig,
        chartResults: this.buildCumulativeChartResults(elecConfig),
        chartColorScheme: this.electricityColorScheme
      };
    } else {
      this.electricityPanel = null;
    }

    if (gasConfig) {
      this.gasPanel = {
        config: gasConfig,
        chartResults: gasConfig.isMapped ? this.buildCumulativeChartResults(gasConfig) : [],
        chartColorScheme: this.gasColorScheme
      };
    } else {
      // Default safe gas placeholder
      this.gasPanel = {
        config: {
          locationId: 'gas-placeholder',
          name: 'Gas',
          utilityType: 'Gas',
          unit: undefined, // Do not claim an unverified unit
          siteId: this.siteId || 0,
          isMapped: false,
          hasEnergy: false,
          hasCurrentPower: false,
          status: 'Awaiting Mapping',
          statusMessage: 'Awaiting client gas mapping'
        } as LiveDisplayOverviewVM,
        chartResults: [],
        chartColorScheme: this.gasColorScheme
      };
    }
  }

  getBaselineWeekdayLabel(utility?: LiveDisplayOverviewVM | null): string {
    const rawDate: any = utility?.baselineDate;
    if (!rawDate) {
      return 'Last Week';
    }

    let d: Date;
    if (rawDate instanceof Date) {
      d = rawDate;
    } else if (typeof rawDate === 'string') {
      const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        d = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10), 12, 0, 0);
      } else {
        d = new Date(rawDate);
      }
    } else {
      d = new Date(rawDate);
    }

    if (isNaN(d.getTime())) {
      return 'Last Week';
    }

    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    return `Last ${weekday}`;
  }

  get lastWeekdayLabel(): string {
    return this.getBaselineWeekdayLabel(this.electricityPanel?.config);
  }

  private buildCumulativeChartResults(utility: LiveDisplayOverviewVM): ChartSeriesGroup[] {
    if (!utility || !utility.isMapped) {
      return [];
    }

    const todayTrend = utility.todayCumulativeTrend || [];
    const baselineTrend = utility.baselineCumulativeTrend || [];

    if (!todayTrend.length && !baselineTrend.length) {
      return [];
    }

    const todaySeries: ChartSeriesItem[] = todayTrend.map(p => ({
      name: p.time || '',
      value: p.value !== undefined && p.value !== null ? Number(p.value.toFixed(2)) : 0
    }));

    const baselineSeries: ChartSeriesItem[] = baselineTrend.map(p => ({
      name: p.time || '',
      value: p.value !== undefined && p.value !== null ? Number(p.value.toFixed(2)) : 0
    }));

    const baselineLabel = this.getBaselineWeekdayLabel(utility);

    return [
      {
        name: 'Today',
        series: todaySeries
      },
      {
        name: baselineLabel,
        series: baselineSeries
      }
    ];
  }

  xAxisTickFormatting = (val: string): string => {
    if (!val) return '';
    const match = val.match(/^(\d{1,2}):00$/);
    if (!match) return '';
    const hour = parseInt(match[1], 10);
    return hour % 3 === 0 ? val : '';
  };

  getStatusClass(status?: string): string {
    if (!status) return 'status-unknown';
    switch (status.toUpperCase()) {
      case 'LIVE':
        return 'status-live';
      case 'DELAYED':
        return 'status-delayed';
      case 'OFFLINE':
        return 'status-offline';
      case 'AWAITING MAPPING':
      case 'AWAITING CLIENT MAPPING':
        return 'status-awaiting';
      default:
        return 'status-unknown';
    }
  }

  getDeltaClass(diffVal?: number | null, changePercent?: number | null): string {
    if (diffVal === undefined || diffVal === null || changePercent === undefined || changePercent === null) {
      return 'delta-neutral';
    }
    if (diffVal < 0 || changePercent < -2) return 'delta-good'; // Using less energy than baseline = good
    if (diffVal > 0 || changePercent > 2) return 'delta-warn';  // Using more energy = warn
    return 'delta-neutral';
  }

  formatValue(val?: number | null): string {
    if (val === undefined || val === null) return '--';
    return val.toFixed(1);
  }

  formatDifference(val?: number | null, unit?: string): string {
    if (val === undefined || val === null) return '--';
    const prefix = val > 0 ? '+' : '';
    return `${prefix}${val.toFixed(1)} ${unit || ''}`.trim();
  }

  formatPercent(val?: number | null): string {
    if (val === undefined || val === null) return 'Comparison unavailable';
    const prefix = val > 0 ? '+' : '';
    return `${prefix}${val.toFixed(1)}%`;
  }

  getCurrencySymbol(currency?: string): string {
    if (!currency) return '€';
    switch (currency.toUpperCase()) {
      case 'EUR':
      case '€':
        return '€';
      case 'GBP':
      case '£':
        return '£';
      case 'USD':
      case '$':
        return '$';
      default:
        return currency;
    }
  }

  formatCost(val?: number | null, currency?: string): string {
    if (val === undefined || val === null) return '';
    const symbol = this.getCurrencySymbol(currency);
    return `${symbol}${val.toFixed(2)}`;
  }

  formatDifferenceCost(val?: number | null, currency?: string): string {
    if (val === undefined || val === null) return '';
    const symbol = this.getCurrencySymbol(currency);
    const rounded = Number(val.toFixed(2));
    if (rounded > 0) {
      return `+${symbol}${rounded.toFixed(2)}`;
    }
    if (rounded < 0) {
      return `-${symbol}${Math.abs(rounded).toFixed(2)}`;
    }
    return `${symbol}0.00`;
  }

  toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      this.isFullscreen = true;
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
        this.isFullscreen = false;
      }
    }
    this.cdr.markForCheck();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'f' || event.key === 'F') {
      this.toggleFullscreen();
    }
  }
}
